import DatePicker from 'react-datepicker';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IContracts } from '@/modules/workforce/Contracts/types';
import {
  ErrorMessage,
  InputField,
  InputLabel,
} from '@/shared/components/atoms';
import SwitchButton from '@/shared/components/atoms/SwitchButton';
import { getLocalizedDate } from '@/shared/lib/dates';

import Clock from '~/images/shared/clock.svg';

function DailyBased() {
  const {
    t,
    i18n: { language },
  } = useTranslation('common');
  const {
    control,
    watch,
    register,
    setValue,
    formState: { errors },
  } = useFormContext<IContracts.IAddContractSchema>();

  const entityWatcher = watch('entity');
  const hasExtraTimeWatcher = watch('dailyBased.hasExtraTime');
  const startTimeWatcher = watch('dailyBased.startTime');
  const endTimeWatcher = watch('dailyBased.endTime');

  return (
    <div className='pt-24'>
      <div className='flex flex-col gap-24'>
        <InputField
          id='rate'
          {...register('dailyBased.rate', {
            setValueAs: (value: string) => (value ? Number(value) : undefined),
          })}
          className='!py-14'
          label={t('Daily Rate')}
          placeholder={t('Daily Rate')}
          width='w-full'
          type='number'
          step={0.01}
          suffix={
            entityWatcher.currency ? (
              <span>{t(entityWatcher.currency)}</span>
            ) : undefined
          }
          error={!!errors?.dailyBased?.rate}
          errorMessage={errors?.dailyBased?.rate?.message}
        />
        <div className='flex w-full flex-col'>
          <InputLabel
            label={t('from')}
            className='capitalize'
            id='dailyBased.startTime'
          />
          <div className='relative'>
            <Controller
              name='dailyBased.startTime'
              control={control}
              render={({ field: { onChange, value, ...field } }) => (
                <DatePicker
                  id='dailyBased.startTime'
                  locale={language}
                  {...field}
                  value={getLocalizedDate({
                    date: value,
                    formatDate: 'hh:mm a',
                    locale: language,
                  })}
                  onChange={(date) => {
                    if (date) onChange(date);
                  }}
                  placeholderText={t('from')}
                  showTimeSelect
                  showTimeSelectOnly
                  selected={startTimeWatcher || null}
                  timeIntervals={15}
                  timeCaption='Time'
                  dateFormat='h:mm aa'
                  className='w-full cursor-pointer rounded-xl border-xs border-background-disable bg-white px-12 !py-14 capitalize'
                />
              )}
            />
            <Clock className='absolute top-1/2 -translate-y-1/2 ltr:right-12 rtl:left-12' />
          </div>
          {errors?.dailyBased?.startTime && (
            <ErrorMessage
              errorMessage={errors.dailyBased?.startTime?.message ?? ''}
            />
          )}
        </div>

        <div className='flex w-full flex-col'>
          <InputLabel
            label={t('to')}
            id='dailyBased.endTime'
            className='capitalize'
          />
          <div className='relative'>
            <Controller
              name='dailyBased.endTime'
              control={control}
              render={({ field: { onChange, value, ...field } }) => (
                <DatePicker
                  id='dailyBased.endTime'
                  value={getLocalizedDate({
                    date: value,
                    formatDate: 'hh:mm a',
                    locale: language,
                  })}
                  locale={language}
                  {...field}
                  onChange={(date) => {
                    if (date) onChange(date);
                  }}
                  placeholderText={t('to')}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  selected={endTimeWatcher || null}
                  timeCaption='Time'
                  dateFormat='h:mm aa'
                  className='w-full cursor-pointer rounded-xl border-xs border-background-disable bg-white px-12 !py-14 capitalize'
                />
              )}
            />
            <Clock className='absolute top-1/2 -translate-y-1/2 ltr:right-12 rtl:left-12' />
          </div>
          {errors?.dailyBased?.endTime && (
            <ErrorMessage
              errorMessage={errors.dailyBased?.endTime?.message ?? ''}
            />
          )}
        </div>
        <span className='h-[1px] w-full bg-neutralVariant-90'></span>
        <SwitchButton
          id='dailyBased.hasExtraTime'
          describtion={t('has-overtime-pay')}
          onChangeEffect={(val) => {
            if (!val)
              setValue('dailyBased.extraHoursPricing', undefined, {
                shouldValidate: true,
              });
          }}
        />

        {hasExtraTimeWatcher && (
          <InputField
            id='dailyBased.extraHoursPricing.0.hourPrice'
            {...register('dailyBased.extraHoursPricing.0.hourPrice', {
              setValueAs: (value: string) =>
                value ? Number(value) : undefined,
            })}
            className='!py-14'
            error={!!errors?.dailyBased?.extraHoursPricing?.[0]?.hourPrice}
            errorMessage={
              errors?.dailyBased?.extraHoursPricing?.[0]?.hourPrice?.message
            }
            label={t('hourly-rate')}
            placeholder={t('hourly-rate')}
            width='w-full'
            type='number'
            step={0.01}
            suffix={
              entityWatcher.currency ? (
                <span>{t(entityWatcher.currency)}</span>
              ) : undefined
            }
          />
        )}
      </div>
    </div>
  );
}

export default DailyBased;
